
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, Calendar, Plus, Edit, AlertCircle, Trash2 } from "lucide-react";
import { ActivityPrice } from "@/hooks/useFinanceData";

interface Activity {
  id: string;
  name: string;
}

interface ActivityPriceCardProps {
  activity: Activity;
  sessionPrice?: ActivityPrice;
  monthlyPrice?: ActivityPrice;
  onEditPrice: (price: ActivityPrice) => void;
  onCreatePrice: (activityId: string, billingType: 'per_session' | 'monthly') => void;
  onDeactivatePrice: (priceId: string) => void;
}

export const ActivityPriceCard = ({ 
  activity, 
  sessionPrice, 
  monthlyPrice, 
  onEditPrice, 
  onCreatePrice,
  onDeactivatePrice
}: ActivityPriceCardProps) => {
  const hasSessionPrice = !!sessionPrice;
  const hasMonthlyPrice = !!monthlyPrice;
  const isCompletelyConfigured = hasSessionPrice && hasMonthlyPrice;

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{activity.name}</CardTitle>
          <div className="flex items-center gap-2">
            {isCompletelyConfigured ? (
              <Badge variant="default" className="bg-green-100 text-green-800 border-green-300">
                Configurado
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Incompleto
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Precio por día */}
        <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium">Por Día</span>
          </div>
          <div className="flex items-center gap-2">
            {hasSessionPrice ? (
              <>
                <Badge variant="secondary" className="bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200">
                  {sessionPrice.price_amount}€
                </Badge>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEditPrice(sessionPrice)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => onDeactivatePrice(sessionPrice.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </>
            ) : (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onCreatePrice(activity.id, 'per_session')}
                className="flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                <span className="text-xs">Crear</span>
              </Button>
            )}
          </div>
        </div>

        {/* Precio mensual */}
        <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium">Mensual</span>
          </div>
          <div className="flex items-center gap-2">
            {hasMonthlyPrice ? (
              <>
                <Badge variant="secondary" className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  {monthlyPrice.price_amount}€
                </Badge>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => onEditPrice(monthlyPrice)}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => onDeactivatePrice(monthlyPrice.id)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </>
            ) : (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onCreatePrice(activity.id, 'monthly')}
                className="flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                <span className="text-xs">Crear</span>
              </Button>
            )}
          </div>
        </div>

        {/* Acciones rápidas */}
        {!isCompletelyConfigured && (
          <div className="pt-2 border-t">
            <div className="flex gap-2">
              {!hasSessionPrice && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onCreatePrice(activity.id, 'per_session')}
                  className="flex-1 text-xs"
                >
                  + Precio por día
                </Button>
              )}
              {!hasMonthlyPrice && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => onCreatePrice(activity.id, 'monthly')}
                  className="flex-1 text-xs"
                >
                  + Precio mensual
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
